<template>
  <v-main class="password-reset">
    <v-container class="password-reset__container d-flex align-center justify-center">
      <v-card v-if="token" class="password-reset__card form-canvas-dialog">
        <v-card-title class="px-5 pt-5 pb-2">{{ $t('Password Reset') }}</v-card-title>
        <!-- mt-0 cancels Vuetify's negative margin that pulls a subtitle up under the title -->
        <v-card-subtitle class="px-5 pt-0 pb-3 mt-0">
          {{
            $t(
              'Your password must be at least {min} characters including lowercase and uppercase letters, and digits',
              {
                min: MIN_PASS_LENGTH,
              }
            )
          }}
        </v-card-subtitle>
        <v-card-text class="pb-0">
          <v-form ref="form" v-model="valid" @submit.prevent="submit">
            <v-text-field
              v-model="password"
              outlined
              dense
              autofocus
              autocomplete="new-password"
              :type="showPassword.password ? 'text' : 'password'"
              :prepend-inner-icon="icons.mdiLockOutline"
              :append-icon="showPassword.password ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              :rules="passRules"
              :label="$t('Password')"
              @click:append="showPassword.password = !showPassword.password"
            />
            <v-text-field
              v-model="repeat"
              outlined
              dense
              autocomplete="new-password"
              :type="showPassword.repeat ? 'text' : 'password'"
              :prepend-inner-icon="icons.mdiLockOutline"
              :append-icon="showPassword.repeat ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              :rules="repeatRules"
              :label="$t('Repeat Password')"
              @click:append="showPassword.repeat = !showPassword.repeat"
            />
            <!-- Hidden submit lets Enter submit the form natively -->
            <button type="submit" hidden />
          </v-form>
        </v-card-text>
        <v-card-actions class="px-5 pb-5 pt-2">
          <v-spacer />
          <v-btn depressed color="primary" :loading="loading" @click="submit">{{ $t('Set New Password') }}</v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else class="password-reset__card form-canvas-dialog">
        <v-card-title class="pa-5">{{ $t('Invalid token') }}</v-card-title>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import Dataman from 'App/Dataman';
import { MIN_PASS_LENGTH, makePassRules } from 'Lib/Password';
import { mdiLockOutline, mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js';

export default {
  name: 'PasswordReset',
  data() {
    return {
      icons: {
        mdiLockOutline,
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
      valid: false,
      loading: false,
      password: '',
      repeat: '',
      showPassword: {
        password: false,
        repeat: false,
      },
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    passRules() {
      return makePassRules(this.$t.bind(this));
    },
    repeatRules() {
      return [
        (v) => !!v || this.$t('Please repeat your password'),
        (v) => v === this.password || this.$t('Passwords are not identical'),
      ];
    },
    MIN_PASS_LENGTH() {
      return MIN_PASS_LENGTH;
    },
  },
  methods: {
    submit() {
      if (this.loading || !this.$refs.form.validate()) {
        return false;
      }
      const { token, password } = this;
      this.loading = true;
      Dataman.passwordReset
        .reset({ token, new_password: password })
        .then((res) => {
          if (res.status === 200) {
            this.$router.push('/?login');
          }
        })
        .catch((err) => {
          this.$toaster.error(err?.response?.data['hydra:description'] || err.response?.data?.description, 7e3);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
// Fills the free height of the parent so the card can be centered vertically:
// flex-grow works inside the public template section, min-height inside the logged-in app v-main
.password-reset {
  flex: 1 1 auto;
  min-height: 100%;
  display: flex;

  ::v-deep .v-main__wrap {
    display: flex;
  }
}

.password-reset__container {
  flex: 1 1 auto;
}

.password-reset__card {
  width: 100%;
  max-width: 460px;
}
</style>
